import { useMemo } from 'react'
import {
  getWholesaleCountrySpotConfig,
  normalizeWholesaleCountryIso2,
  wholesaleSpotSeriesChoicesForCountry,
  wholesaleYearChoicesForCountry,
  type WholesaleCountryIso2,
  type WholesaleSpotSeriesChoice,
} from './wholesaleTariffTypes'

export type TariffDataWholesaleSlice =
  | {
      wholesale?: boolean | string
      wholesale_country_iso2?: unknown
    }
  | null
  | undefined

export type WholesaleTariffDerivedFromTariffData = {
  wholesaleEnabled: boolean
  wholesaleCountryIso2: WholesaleCountryIso2
  wholesaleSpotSeriesChoices: WholesaleSpotSeriesChoice[]
  wholesaleCountrySpotConfig: ReturnType<typeof getWholesaleCountrySpotConfig>
  wholesalePriceYearChoices: ReturnType<typeof wholesaleYearChoicesForCountry>
}

export function useWholesaleTariffDerivedFromTariffData(
  tariffData: TariffDataWholesaleSlice
): WholesaleTariffDerivedFromTariffData {
  const wholesaleEnabled = tariffData?.wholesale === true || tariffData?.wholesale === 'true'

  const wholesaleCountryIso2 = useMemo(
    () => normalizeWholesaleCountryIso2(tariffData?.wholesale_country_iso2),
    [tariffData?.wholesale_country_iso2]
  )

  const wholesaleSpotSeriesChoices = useMemo(
    () => wholesaleSpotSeriesChoicesForCountry(wholesaleCountryIso2),
    [wholesaleCountryIso2]
  )

  const wholesaleCountrySpotConfig = useMemo(
    () => getWholesaleCountrySpotConfig(wholesaleCountryIso2),
    [wholesaleCountryIso2]
  )

  const wholesalePriceYearChoices = useMemo(
    () => wholesaleYearChoicesForCountry(wholesaleCountryIso2),
    [wholesaleCountryIso2]
  )

  return useMemo(
    () => ({
      wholesaleEnabled,
      wholesaleCountryIso2,
      wholesaleSpotSeriesChoices,
      wholesaleCountrySpotConfig,
      wholesalePriceYearChoices,
    }),
    [
      wholesaleCountryIso2,
      wholesaleCountrySpotConfig,
      wholesaleEnabled,
      wholesalePriceYearChoices,
      wholesaleSpotSeriesChoices,
    ]
  )
}
